import { NavLink } from 'react-router-dom';
import {
  LayoutGrid,
  BarChart3,
  Image,
  Video,
  Music,
  Star,
} from 'lucide-react';
import { useAppStore } from '../../store/appStore';

const categories = [
  { value: 'character', label: '角色', icon: '👤' },
  { value: 'scene', label: '场景', icon: '🏞' },
  { value: 'camera', label: '运镜', icon: '🎥' },
  { value: 'prop', label: '道具', icon: '📦' },
  { value: 'style', label: '画风', icon: '🎨' },
  { value: 'shot', label: '分镜', icon: '🎬' },
];

export function Sidebar() {
  const { filters, setFilters, fetchAssets } = useAppStore();

  const handleCategoryClick = (category?: string) => {
    // 保留当前 media_type，只切换 category
    const next = { ...filters, category, media_type: filters.media_type, offset: 0 };
    setFilters(next);
    fetchAssets(next);
  };

  const handleMediaTypeClick = (mediaType?: 'image' | 'video' | 'audio') => {
    // 保留当前 category，只切换 media_type
    const next = { ...filters, media_type: mediaType, category: filters.category, offset: 0 };
    setFilters(next);
    fetchAssets(next);
  };

  return (
    <aside className="w-60 border-r border-slate-800/50 bg-slate-900/30 flex flex-col">
      {/* Brand */}
      <div className="p-5 border-b border-slate-800/50">
        <h1 className="text-lg font-semibold tracking-tight">
          <span className="text-violet-400">V</span>espera
          <span className="text-slate-500 text-sm ml-2 font-normal">暮序</span>
        </h1>
        <p className="text-xs text-slate-500 mt-1">万物生成，归而有序</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        <NavLink
          to="/"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
              isActive ? 'bg-violet-500/10 text-violet-300' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
            }`
          }
          onClick={() => {
            const next = { ...filters, category: undefined, media_type: undefined, is_favorite: false, offset: 0 };
            setFilters(next);
            fetchAssets(next);
          }}
        >
          <LayoutGrid size={16} />
          全部资产
        </NavLink>

        <NavLink
          to="/"
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 transition-colors"
          onClick={() => {
            const next = { ...filters, category: filters.category, media_type: filters.media_type, is_favorite: true, offset: 0 };
            setFilters(next);
            fetchAssets(next);
          }}
        >
          <Star size={16} />
          收藏夹
        </NavLink>

        <NavLink
          to="/stats"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
              isActive ? 'bg-violet-500/10 text-violet-300' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
            }`
          }
        >
          <BarChart3 size={16} />
          统计概览
        </NavLink>

        {/* Divider */}
        <div className="pt-4 pb-2">
          <p className="px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">分类</p>
        </div>

        {categories.map((cat) => (
          <button
            key={cat.value}
            onClick={() => handleCategoryClick(cat.value)}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
              filters.category === cat.value
                ? 'bg-violet-500/10 text-violet-300'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
            }`}
          >
            <span>{cat.icon}</span>
            {cat.label}
          </button>
        ))}

        {/* Divider */}
        <div className="pt-4 pb-2">
          <p className="px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">媒体类型</p>
        </div>

        {[
          { value: 'image' as const, label: '图像', icon: Image },
          { value: 'video' as const, label: '视频', icon: Video },
          { value: 'audio' as const, label: '音频', icon: Music },
        ].map((type) => (
          <button
            key={type.value}
            onClick={() => handleMediaTypeClick(type.value)}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
              filters.media_type === type.value
                ? 'bg-violet-500/10 text-violet-300'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
            }`}
          >
            <type.icon size={16} />
            {type.label}
          </button>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-slate-800/50">
        <p className="text-xs text-slate-600 text-center">
          Vespera v1.0 · Local-First
        </p>
        {/* 备案号行（ICP + 公安联网，均已办结；flex-wrap 防窄侧栏溢出，顺序 ICP 在前） */}
        <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-slate-600">
          {/* ICP 备案号（无图标，链接工信部） */}
          <a
            href="https://beian.miit.gov.cn"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 transition-colors hover:text-slate-400"
          >
            滇ICP备2026016982号
          </a>
          {/* 公安联网备案号（警徽自托管 /beian-police.png） */}
          <a
            href="https://beian.mps.gov.cn/#/query/webSearch?code=53032802000193"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-xs text-slate-600 transition-colors hover:text-slate-400"
          >
            <img
              src="/beian-police.png"
              alt="公安联网备案"
              className="h-[14px] w-auto opacity-90"
            />
            滇公网安备53032802000193号
          </a>
        </div>
      </div>
    </aside>
  );
}
